import React, { useEffect, useState, useRef } from "react";
import "./friends.css";
import {
  apiSearchUsers,
  apiGetFriends,
  apiGetFriendRequests,
  apiRespondFriendRequest,
  apiSendFriendRequest,
  apiGetSessionInvites,
  apiRespondSessionInvite,
} from "../lib/authApi";

export default function FriendsPage({
  currentUser,
  token,
  onInviteFriends,
  onAcceptSessionInvite,
  onStartSoloSession,
  onLogout,
  onOpenProviders, // ⬅️ NIEUW
}) {
  // We vertrouwen op de token uit AuthContext (App.js)
  const safeToken = token || null;


    // refs voor user-chip en dropdown-menu
  const userChipRef = useRef(null);
  const userMenuRef = useRef(null);

  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // requestId → "accepting" | "accepted" | "rejecting" | "rejected"
  const [friendRequestActionStatus, setFriendRequestActionStatus] = useState(
    {}
  );

  const [sessionInvites, setSessionInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  const [selectedFriendIds, setSelectedFriendIds] = useState(new Set());
  const [inviteBusy, setInviteBusy] = useState(false);

  // voor UI: bij wie is net een request verstuurd?
  const [sentUserIds, setSentUserIds] = useState(new Set());


    // --- Klik buiten user-menu -> menu dicht ---
  useEffect(() => {
    if (!showUserMenu) return;

    function handleClickOutside(event) {
      const chipEl = userChipRef.current;
      const menuEl = userMenuRef.current;

      // Als de klik binnen chip of menu is, niks doen
      if (
        (chipEl && chipEl.contains(event.target)) ||
        (menuEl && menuEl.contains(event.target))
      ) {
        return;
      }

      // Anders menu sluiten
      setShowUserMenu(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showUserMenu]);


  // ------- Helpers: vrienden + requests laden -------

  async function loadFriendsAndRequests() {
    if (!safeToken) return;
    try {
      const [friendsRes, frRes] = await Promise.all([
        apiGetFriends(safeToken),
        apiGetFriendRequests(safeToken),
      ]);
      setFriends(friendsRes?.friends || []);
      setFriendRequests(frRes?.requests || []);
    } catch (e) {
      console.warn("[Bond] loadFriendsAndRequests error", e);
    }
  }

  // ------- Session-invites laden (met smooth refresh) -------

  async function reloadSessionInvites(options = {}) {
    const { initial = false } = options;
    if (!safeToken) return;

    if (initial) {
      setLoadingInvites(true);
    }

    try {
      const inv = await apiGetSessionInvites(safeToken);
      setSessionInvites(inv?.invites || []);
    } catch (e) {
      console.warn("[Bond] reloadSessionInvites error", e);
    } finally {
      if (initial) {
        setLoadingInvites(false);
      }
    }
  }

  // Init + auto-refresh elke 12s (zonder flits)
  useEffect(() => {
    if (!safeToken) return;

    // Eerste load: met loading-state
    loadFriendsAndRequests();
    reloadSessionInvites({ initial: true });

    // Poll: stille refresh, geen "laden..." → geen flits
    const id = setInterval(() => {
      loadFriendsAndRequests();
      reloadSessionInvites();
    }, 12000);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeToken]);

  // ------- Users zoeken -------

  async function handleSearch(e) {
    const value = e.target.value;
    setSearchQuery(value);
    setSearchError("");

    const trimmed = value.trim();

    // Als de input leeg is, of te kort (<2 chars) → reset en niet zoeken
    if (!safeToken || trimmed.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    const res = await apiSearchUsers(trimmed, safeToken);
    const rawUsers = res?.users || [];

    const q = trimmed.toLowerCase();

    // kleine helper: pak weergavenaam zoals je die al gebruikt
    function displayNameFor(u) {
      const id = String(u.id);
      return (u.name || u.username || u.email || id || "").trim();
    }

    // score: hoe beter de match, hoe hoger
    function matchScore(name, q) {
      const n = name.toLowerCase();

      if (!n.includes(q)) return -1; // helemaal geen match → killen
      if (n === q) return 100; // exact gelijk
      if (n.startsWith(q)) return 90; // begint met
      // check of een woord begint met q (bv. "Remi Koek" op "koek")
      const words = n.split(/\s+/);
      if (words.some((w) => w.startsWith(q))) return 80;
      return 50; // ergens middenin
    }

    // eerst scoren + filteren, dan sorteren, dan max 3
    const scored = rawUsers
      .map((u) => {
        const name = displayNameFor(u);
        return { user: u, name, score: matchScore(name, q) };
      })
      .filter((x) => x.score >= 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3) // max 3 suggesties
      .map((x) => x.user);

    setSearchResults(scored);
  }

  // ------- Vriendschapsverzoek sturen -------

  async function handleSendFriendRequest(userId) {
    if (!safeToken) return;
    try {
      await apiSendFriendRequest(userId, safeToken);

      // UI: direct “✔ Verstuurd”
      setSentUserIds((prev) => {
        const next = new Set(prev);
        next.add(String(userId));
        return next;
      });

      await loadFriendsAndRequests();
    } catch (e) {
      console.warn("[Bond] sendFriendRequest error", e);
    }
  }

  // ------- Friend-requests accepteren / afwijzen -------

  async function handleFriendRequestAction(requestId, action) {
    if (!safeToken) return;

    // 1) UI-status: we zijn bezig met deze request
    setFriendRequestActionStatus((prev) => ({
      ...prev,
      [requestId]: action === "accept" ? "accepting" : "rejecting",
    }));

    try {
      await apiRespondFriendRequest(requestId, action, safeToken);

      // 2) Na succes: markeer als accepted / rejected
      setFriendRequestActionStatus((prev) => ({
        ...prev,
        [requestId]: action === "accept" ? "accepted" : "rejected",
      }));

      // 3) Reload vrienden + verzoeken, maar zonder await
      //    zodat de UI eerst even "Geaccepteerd ✓" kan tonen
      loadFriendsAndRequests();
    } catch (e) {
      console.warn("[Bond] respondFriendRequest error", e);
      // 4) Bij error → status terugzetten
      setFriendRequestActionStatus((prev) => {
        const copy = { ...prev };
        delete copy[requestId];
        return copy;
      });
    }
  }

  // ------- Sessie-uitnodigingen -------

  async function handleSessionInviteAction(invite, action) {
    if (!safeToken) return;
    try {
      const res = await apiRespondSessionInvite(
        invite.id,
        action,
        safeToken
      );
      if (res?.ok && res.invite) {
        setSessionInvites((prev) =>
          prev.map((x) => (String(x.id) === String(invite.id) ? res.invite : x))
        );
      } else {
        await reloadSessionInvites();
      }

      if (
        action === "accept" &&
        onAcceptSessionInvite &&
        invite.sessionCode
      ) {
        onAcceptSessionInvite(invite.sessionCode);
      }
    } catch (e) {
      console.warn("[Bond] respondSessionInvite error", e);
    }
  }

  // Rejoin van een eerder geaccepteerde sessie (geen extra API-call nodig)
  function handleRejoinSession(invite) {
    if (!onAcceptSessionInvite || !invite?.sessionCode) return;
    onAcceptSessionInvite(invite.sessionCode);
  }

  // ------- Menu-acties: providers + uitloggen -------

  function handleOpenProviders() {
    // dropdown dichtklappen
    setShowUserMenu(false);

    if (onOpenProviders) {
      onOpenProviders();
    }
  }

  function handleLogout() {
    // dropdown dichtklappen
    setShowUserMenu(false);

    // App.js laten afhandelen wat er precies met auth gebeurt
    if (onLogout) {
      onLogout();
    }
  }

  // ------- Vrienden selecteren & invites sturen (voor sessie) -------

  function toggleFriendSelected(friendId) {
    setSelectedFriendIds((prev) => {
      const next = new Set(prev);
      if (next.has(friendId)) next.delete(friendId);
      else next.add(friendId);
      return next;
    });
  }

  async function handleSendSessionInvites() {
    if (!onInviteFriends) return;
    const ids = Array.from(selectedFriendIds);
    if (!ids.length) return;
    setInviteBusy(true);
    try {
      await onInviteFriends(ids);
      setSelectedFriendIds(new Set());
      await reloadSessionInvites();
    } finally {
      setInviteBusy(false);
    }
  }

  async function handleStartSoloSession() {
    // Solo Bondsessie starten (zonder invites)
    if (!onStartSoloSession) return;
    try {
      await onStartSoloSession();
    } catch (e) {
      console.warn("[Bond] startSoloSession error", e);
    }
  }

  // ------- Render helpers -------

  function renderInviteStatus(inv) {
    if (inv.status === "accepted") return "✔ Geaccepteerd";
    if (inv.status === "declined") return "✖ Afgewezen";
    return "Status: Nieuw";
  }

  function avatarInitial(name) {
    if (!name) return "?";
    return name.trim().charAt(0).toUpperCase();
  }

  return (
    <div className="friends-page">
      {/* Header-balk */}
      <header className="friends-header-bar">
        <div className="friends-header-left">
          <span className="friends-logo">BOND</span>
          <span className="friends-logo-pill">Friends</span>
        </div>

        {currentUser && (
        <div
  className="friends-header-right"
  style={{ position: "relative" }}
>
  {/* Klikbare user-chip */}
  <div
    className="friends-user-chip"
    ref={userChipRef}
    style={{ cursor: "pointer" }}
    onClick={() => setShowUserMenu((v) => !v)}
  >

              <div className="friends-user-avatar">
                {avatarInitial(currentUser.name || currentUser.email)}
              </div>
              <div className="friends-user-text">
                <div className="friends-user-label">Ingelogd als</div>
                <div className="friends-user-name">
                  {currentUser.name || "Bond user"}
                </div>
                {currentUser.email && (
                  <div className="friends-user-email">
                    {currentUser.email}
                  </div>
                )}
              </div>
            </div>

            {/* Dropdown-menu */}
            {showUserMenu && (
  <div
    className="friends-user-menu"
    ref={userMenuRef}
    style={{
      position: "absolute",
      top: "100%",
      right: 0,
      marginTop: 8,
      background: "#0f172a",
      border: "1px solid rgba(148,163,184,0.22)",
      borderRadius: 12,
      padding: "10px 0",
      minWidth: 180,
      boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
      zIndex: 20,
    }}
  >

                <button
                  onClick={handleOpenProviders}
                  style={{
                    width: "100%",
                    background: "none",
                    border: "none",
                    color: "#e5e7eb",
                    padding: "10px 14px",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: 14,
                  }}
                >
                  Mijn abonnementen
                </button>

                <div
                  style={{
                    height: 1,
                    background: "rgba(31,41,55,0.9)",
                    margin: "4px 0",
                  }}
                />

                <button
                  onClick={handleLogout}
                  style={{
                    width: "100%",
                    background: "none",
                    border: "none",
                    color: "#e5e7eb",
                    padding: "10px 14px",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: 14,
                  }}
                >
                  Uitloggen
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      <main className="friends-main">
        <div className="friends-grid-top">
          {/* Je vrienden */}
          <section className="friends-card friends-card-left">
            <div className="friends-card-header-row">
              <div>
                <h2 className="friends-card-title">Je vrienden</h2>
                <p className="friends-card-sub">
                  Selecteer vrienden om ze uit te nodigen voor je Bondsessie.
                  <br />
                  <br />
                  Klik op &quot;Invite sturen&quot; om automatisch een sessie te
                  starten en invites te versturen. Klik op &quot;Start Solo
                  Bondsessie&quot; om zelf alvast een sessie te starten.
                </p>
              </div>
            </div>

            <div className="friends-actions-row">
              <button
                type="button"
                className="friends-pill-btn"
                onClick={handleStartSoloSession}
              >
                Start Solo Bondsessie
              </button>
              <button
                type="button"
                className="friends-pill-btn friends-pill-btn-primary"
                disabled={selectedFriendIds.size === 0 || inviteBusy}
                onClick={handleSendSessionInvites}
              >
                {inviteBusy ? "Bezig..." : "Invite sturen"}
              </button>
            </div>

            {friends.length === 0 && (
              <p className="friends-empty">
                Je hebt nog geen Bond-vrienden. Zoek een user en stuur een
                vriendschapsverzoek.
              </p>
            )}

            {friends.length > 0 && (
              <div className="friends-scroll friends-scroll-friends">
                <ul className="friends-list">
                  {friends.map((f) => {
                    const id = String(f.id);
                    const selected = selectedFriendIds.has(id);
                    const displayName =
                      f.name || f.username || f.email || id;
                    return (
                      <li
                        key={id}
                        className={
                          "friends-list-item" +
                          (selected ? " friends-list-item-selected" : "")
                        }
                        onClick={() => toggleFriendSelected(id)}
                      >
                        <div className="friends-list-main">
                          <div className="friends-avatar">
                            {avatarInitial(displayName)}
                          </div>
                          <div className="friends-list-text">
                            <div className="friends-list-name">
                              {displayName}
                            </div>
                          </div>
                        </div>

                        <div className="friends-list-right">
                          <span className="friends-ready-pill">
                            Klaar voor Bond
                          </span>
                          <button
                            type="button"
                            className={
                              "friends-checkbox" +
                              (selected ? " friends-checkbox-on" : "")
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFriendSelected(id);
                            }}
                          >
                            {selected ? "✓" : ""}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </section>

          {/* Sessie-uitnodigingen */}
          <section className="friends-card friends-card-middle">
            <div className="friends-card-header">
              <h2 className="friends-card-title">Sessie-uitnodigingen</h2>
              <p className="friends-card-sub">
                Accepteer invites en join de Bondsessie.
              </p>
            </div>

            {loadingInvites && (
              <p className="friends-empty">
                Sessie-uitnodigingen laden...
              </p>
            )}

            {!loadingInvites && sessionInvites.length === 0 && (
              <p className="friends-empty">
                Je hebt nog geen sessie-uitnodigingen.
              </p>
            )}

            {!loadingInvites && sessionInvites.length > 0 && (
              <div className="friends-scroll friends-scroll-invites">
                <ul className="friends-list">
                  {sessionInvites.map((inv) => (
                    <li key={inv.id} className="friends-item-row">
                      <div className="friends-item-main">
                        <div className="friends-avatar">
                          {avatarInitial(inv.from?.name)}
                        </div>
                        <div className="friends-item-text">
                          <div className="friends-item-title">
                            {inv.from?.name || "Onbekende gebruiker"}
                          </div>
                          <div className="friends-item-sub">
                            {renderInviteStatus(inv)}
                          </div>
                        </div>
                      </div>

                      <div className="friends-item-actions">
                        {inv.status === "pending" && (
                          <>
                            <button
                              type="button"
                              className="friends-chip accept"
                              onClick={() =>
                                handleSessionInviteAction(inv, "accept")
                              }
                            >
                              Accepteren
                            </button>
                            <button
                              type="button"
                              className="friends-chip decline"
                              onClick={() =>
                                handleSessionInviteAction(inv, "decline")
                              }
                            >
                              Afwijzen
                            </button>
                          </>
                        )}

                        {inv.status === "accepted" && (
                          <button
                            type="button"
                            className="friends-chip friends-chip-primary"
                            onClick={() => handleRejoinSession(inv)}
                          >
                            Opnieuw joinen
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* Vriendschapsverzoeken */}
          <section className="friends-card friends-card-right">
            <div className="friends-card-header">
              <h2 className="friends-card-title">Vriendschapsverzoeken</h2>
              <p className="friends-card-sub">
                Keur verzoeken goed of wijs ze af. Geaccepteerde vrienden komen
                in je lijst.
              </p>
            </div>

            {friendRequests.length === 0 && (
              <p className="friends-empty">
                Je hebt nu geen openstaande vriendschapsverzoeken.
              </p>
            )}

            {friendRequests.length > 0 && (
              <div className="friends-scroll friends-scroll-requests">
                <ul className="friends-list">
                  {friendRequests.map((r) => {
                    const status = friendRequestActionStatus[r.id];
                    const isAccepted =
                      status === "accepted" || r.status === "accepted";

                    return (
                      <li key={r.id} className="friends-item-row">
                        <div className="friends-item-main">
                          <div className="friends-avatar">
                            {avatarInitial(r.from?.name)}
                          </div>
                          <div className="friends-item-text">
                            <div className="friends-item-title">
                              {r.from?.name || "Onbekende gebruiker"}
                            </div>
                            <div className="friends-item-sub">
                              heeft je een vriendschapsverzoek gestuurd.
                            </div>
                          </div>
                        </div>

                        <div className="friends-item-actions">
                          {isAccepted ? (
                            // ✅ Al geaccepteerd: alleen status tonen
                            <span className="friends-status-done">
                              ✔ Geaccepteerd
                            </span>
                          ) : (
                            // Nog niet geaccepteerd → knoppen tonen
                            <>
                              <button
                                type="button"
                                className="friends-chip accept"
                                onClick={() =>
                                  handleFriendRequestAction(
                                    r.id,
                                    "accept"
                                  )
                                }
                              >
                                Accepteren
                              </button>
                              <button
                                type="button"
                                className="friends-chip decline"
                                onClick={() =>
                                  handleFriendRequestAction(
                                    r.id,
                                    "decline"
                                  )
                                }
                              >
                                Afwijzen
                              </button>
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </section>
        </div>

        {/* Zoek Bondusers */}
        <section className="friends-card friends-card-search">
          <div className="friends-card-header">
            <h2 className="friends-card-title">Zoek Bondusers</h2>
            <p className="friends-card-sub">
              Zoek op naam (username) en stuur een vriendschapsverzoek.
            </p>
          </div>

          <div className="friends-search-row">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              className="friends-search-input"
              placeholder="Bijv. RemiKoek"
            />
          </div>

          {searchError && <p className="friends-error">{searchError}</p>}

          {searchResults.length > 0 && (
            <div className="friends-scroll friends-scroll-search">
              <ul className="friends-list">
                {searchResults.map((u) => {
                  const id = String(u.id);
                  const displayName =
                    u.name || u.username || u.email || id;
                  const sent = sentUserIds.has(id);
                  return (
                    <li key={id} className="friends-list-item">
                      <div className="friends-list-main">
                        <div className="friends-avatar">
                          {avatarInitial(displayName)}
                        </div>
                        <div className="friends-list-text">
                          <div className="friends-list-name">
                            {displayName}
                          </div>
                        </div>
                      </div>
                      <div className="friends-item-actions">
                        {sent ? (
                          <span className="friends-status-done">
                            ✔ Verstuurd
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="friends-chip friends-chip-primary"
                            onClick={() =>
                              handleSendFriendRequest(u.id)
                            }
                          >
                            Vriendschapsverzoek sturen
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {searchQuery.trim().length >= 2 &&
            searchResults.length === 0 &&
            !searchError &&
            !searchLoading && (
              <p className="friends-empty">
                Geen gebruikers gevonden voor deze zoekterm.
              </p>
            )}
        </section>
      </main>
    </div>
  );
}
